/* ============================================================================
   LA COLA DE CAMBIOS

   ═══ SE GUARDA ENTERA, Y SE DEDUPLICA POR REGISTRO ═══

   Un piloto puede editar el mismo asiento cinco veces sin señal. Mandar las
   cinco versiones no aporta nada: la última es la que vale y las anteriores
   solo hacen que la primera subida tarde más. Se conserva **una** por
   `entidad + id`, la de marca más nueva.

   La excepción es la confirmación: si una versión fue confirmada por el piloto,
   esa marca no se pierde aunque llegue otra edición posterior. La confirmación
   es una decisión humana, no un detalle de orden.

   ═══ NADA SALE DE LA COLA HASTA QUE EL SERVIDOR CONTESTE ═══

   `quitarDeLaCola` se llama con los ids que el servidor nombró —aplicados,
   descartados o rechazados— y nunca antes. Si el envío se corta a la mitad, la
   cola sigue teniendo todo lo que no se confirmó.
   ========================================================================== */

export const MAXIMO_POR_ENVIO = 200;

export function mismoCambio(a, b) {
    return a?.entidad === b?.entidad && String(a?.id) === String(b?.id);
}

/**
 * Agrega o reemplaza un cambio en la cola.
 *
 * Devuelve una cola NUEVA: el motor la guarda después. Mutar la anterior haría
 * que una escritura fallida dejara la cola en memoria distinta de la guardada.
 */
export function encolarCambio(cola, cambio) {
    if (!cambio?.entidad || !cambio?.id) return cola;
    const indice = cola.findIndex((c) => mismoCambio(c, cambio));
    if (indice < 0) return [...cola, cambio];

    const anterior = cola[indice];
    const masNuevo = Number(cambio.actualizado_en) >= Number(anterior.actualizado_en)
        ? cambio
        : anterior;
    const confirmado = Boolean(anterior.confirmado || cambio.confirmado);
    const siguiente = { ...masNuevo };
    if (confirmado) siguiente.confirmado = true;

    const copia = [...cola];
    copia[indice] = siguiente;
    return copia;
}

export function quitarDeLaCola(cola, idsPorEntidad) {
    return cola.filter((c) => !idsPorEntidad.get(c.entidad)?.has(String(c.id)));
}

export function tandaDeEnvio(cola, maximo = MAXIMO_POR_ENVIO) {
    return cola.slice(0, maximo);
}

/** Cambios entre dos listas, por `id`. Lo que desapareció es una baja. */
export function diferenciasDeLista(antes, despues, { entidad, ahora }) {
    const previos = new Map((Array.isArray(antes) ? antes : []).map((r) => [String(r?.id), r]));
    const siguientes = new Map((Array.isArray(despues) ? despues : []).map((r) => [String(r?.id), r]));
    const cambios = [];

    for (const [id, registro] of siguientes) {
        if (!id || id === "undefined") continue;
        const anterior = previos.get(id);
        if (!anterior) {
            cambios.push({ entidad, id, datos: registro, actualizado_en: ahora });
            continue;
        }
        if (JSON.stringify(anterior) !== JSON.stringify(registro)) {
            cambios.push({ entidad, id, datos: registro, actualizado_en: ahora });
        }
    }

    for (const id of previos.keys()) {
        if (!id || id === "undefined" || siguientes.has(id)) continue;
        cambios.push({ entidad, id, datos: {}, actualizado_en: ahora, borrado_en: ahora });
    }

    return cambios;
}
